import React, { ComponentType, CSSProperties, SVGProps } from 'react';
import c from 'classnames';
import {
  resolveColorToken,
  type RampColorToken,
  type TextColorType,
} from '../../helpers/colorTokens';
import { getSvgByName, IconName } from './getSvgByName';
import styles from './Icon.module.css';

type IconSizes = 'xs' | 's' | 'm' | 'l' | 'xl' | number;

type BaseIconProps = {
  responsive?: boolean;
  className?: string;
  size?: IconSizes;
  active?: boolean;
  preserveColors?: boolean;
  /**
   * Tint, as either a semantic name (preferred, same vocabulary as Typography)
   * or a raw ramp token for steps the semantic names cannot reach. Omitted, the
   * icon keeps its default `--cold-grey-900`. Ignored when `preserveColors` is
   * set, and `active` still wins over it.
   */
  color?: TextColorType | RampColorToken;
};

type IconWithNameProps = BaseIconProps & {
  name: IconName;
  Svg?: never; // Disallow Svg when name is provided
};

type IconWithSvgProps = BaseIconProps & {
  Svg: ComponentType<SVGProps<SVGSVGElement>>;
  name?: never; // Disallow name when Svg is provided
};

type IconProps = IconWithNameProps | IconWithSvgProps;

export const Icon = ({
  name,
  Svg: CustomSvg,
  responsive,
  className = '',
  size = 'm',
  active = false,
  preserveColors = false,
  color,
}: IconProps) => {
  // If name is provided, get the SVG from the lookup
  // Otherwise, use the provided custom SVG
  const SvgComponent = name ? getSvgByName(name) : CustomSvg;

  if (!SvgComponent) {
    console.error('Icon: Either name or Svg must be provided');
    return null;
  }

  return (
    <span
      className={c(className, styles.icon)}
      // Set on the wrapper rather than the svg so the custom property is
      // inherited by every path inside it, whatever the artwork's structure.
      // A semantic name resolves through COLOR_TOKEN; anything else is already a
      // ramp token and is used as-is.
      style={
        color
          ? ({
              '--icon-color': `var(--${
                resolveColorToken(color)
              })`,
            } as CSSProperties)
          : undefined
      }
    >
      <SvgComponent
        className={c(
          active && styles.active,
          !preserveColors && styles.iconSvg,
          typeof size !== 'number' ? styles[size] : null,
          responsive && styles.responsive,
        )}
        style={{
          height: typeof size === 'number' ? `${size}px` : undefined,
          width: typeof size === 'number' ? `${size}px` : undefined,
        }}
        data-testid={name ? `icon-${name}` : 'custom-icon'}
        role='img'
      />
    </span>
  );
};
